/**
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/PromptsAPI',
	'../../../lib/@waca/core-client/js/core-client/nls/StringResources',
	'../../../widgets/livewidget/nls/StringResources',
	'./controls/NonNumericPromptDialog',
	'./controls/NumericPromptDialog',
	'./controls/TextInputPromptDialog',
	'./controls/DateTimePromptDialog',
	'../../../lib/@waca/dashboard-common/dist/ui/dialogs/MessageDialog',
	'underscore'
], (APIFactory, PromptsAPI, StringResources, LiveWidgetStringResources,
	NonNumericPromptDialog, NumericPromptDialog, TextInputPromptDialog, DateTimePromptDialog, MessageDialog, _) => {
	class Prompts {

		SEARCH_AND_SELECT = 'searchAndSelect';
		INPUT_BOX = 'inputBox';
		CALENDAR = 'calendar';
		RANGE = 'range';
		CANCEL = 'cancelPromptDialog';
		UNSUPPORTED_PROMPT_TYPE = 'unSupportedPromptType';
		PROMPT_PROPERTIES = ['name', 'capabilities', 'dataType', 'modelFilterItem'];
		SAVED_PROMPT_PROPERTIES = ['name', 'capabilities', 'dataType', 'values', 'modelFilterItem'];

		/**
		 * @classdesc  Prompt manager class to handle prompt related gestures.
		 * @constructs
		 * @public
		 *
		 */
		constructor(options) {
			this.dashboard = options.features['API'];
			this.logger = options.features['Logger'];

			// In progress prompt view
			this._inProgressMap = {};

			// Pending prompt cache
			this._pendingPrompts = [];

			const preferences = this.dashboard.getGlassCoreSvc('.UserProfile').preferences;
			this._timeZone = preferences.timeZoneID || 'America/New_York';
			this._locale = preferences.productLocale || 'en';
		}

		destroy() {
			if (this.promptDialog) {
				this.promptDialog = null;
			}
			this.dashboard = null;
			this._pendingPrompts = null;
			this._timeZone = null;
			this._locale = null;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [PromptsAPI]);
			}
			return this.api;
		}

		/**
		 * @public
		 * @return true if the response status is prompt fault.
		 */
		isPromptFault(errorResponse) {
			return errorResponse.status === 412 && errorResponse.responseJSON && errorResponse.responseJSON.errors && errorResponse.responseJSON.errors.length;
		}

		openPromptView(specList, visualization, isPreview = false) {
			if (!specList || !specList.length) {
				this.logger.error('Invalid prompt spec');
				return Promise.reject();
			}

			const dataSource = this._getDataSource(specList, visualization);
			if (!dataSource) {
				// Couldn't find right data source. Throw unsupported error.
				this.logger.error('Cannon find related data source for selected prompts');
				return this._onUnSupportedPrompt('missingDataSource');
			}

			const infoList = this._buildPromptInfoList(specList, visualization);
			// If the visualization is preview and there are still unresolved prompts, reject here, Explore will display static view for this preview

			//TODO: preview will be handled differently in future but we need to know to bypass prompting for things like the explore recommender/related vis page.
			//For now, its either passed as an argument or via the internal visualization isPreview api.
			const isVisPreview = isPreview || (visualization.isPreview && visualization.isPreview());
			if (infoList.length > 0 && isVisPreview) {
				const hasUnresolvedPrompt = infoList.some(info => {
					return !info.values;
				});
				if (hasUnresolvedPrompt) {
					return Promise.reject(new Error('promptingIsDisabled'));
				}
			}
			const specNameList = _.pluck(infoList, 'name');
			const promptViewPromiseList = [];
			infoList.forEach(info => {
				const dlgOptions = {};
				dlgOptions.info = info;
				if (info.showView) {
					promptViewPromiseList.push(this._showPromptView(dlgOptions, specNameList, dataSource.getId()));
				} else {
					promptViewPromiseList.push(Promise.resolve(info));
				}

			});

			return Promise.all(promptViewPromiseList)
				.then(resolvedPrompts => {
					const parameterValues = [];
					_.each(resolvedPrompts, resolvedPrompt => {
						parameterValues.push(resolvedPrompt);
					});

					return parameterValues;
				})
				.catch(error => {
					if (error && error.name) {
						this._removeInProgressAndExecuteNext(error.name);
					}
					throw error; // re-throw
				})
				.catch(jqXHR => {
				// Possible query Error. Need to clean local cache.
					const keys = _.keys(this._inProgressMap);
					if (keys && keys.length) {
					// There is only one entry in cache
						this._removeInProgressAndExecuteNext(keys[0]);
					}

					throw jqXHR;
				});
		}

		getPromptSpecList(errorResponse) {
			const error = errorResponse.responseJSON.errors[0];
			if (!error || !error.code) {
				return;
			}

			const params = error.parameters;
			if (!params || params.length === 0) {
				return;
			}

			const paramInfoList = _.map(params, param => {
				param.value.name = param.name;
				return param.value;
			});
			return paramInfoList;
		}

		_getDataSource(specList, visualization) {
			let dataSource = null;
			if (visualization) {
				dataSource = visualization.getDataSource();
			} else {
				const modelFilterItem = _.find(specList, spec => spec.modelFilterItem);
				if (modelFilterItem) {
					const dataSources = this.dashboard.getFeature('DataSources').getDataSourceList();
					dataSource = dataSources.find(source => {
						return source.getMetadataColumn(modelFilterItem);
					});
				}
			}

			return dataSource;
		}

		// TODO remove onCancelPromptDialog and onUnSupportedPrompt from DeprecatedWidgetBase once
		// new API is live
		_onCancelPromptDialog() {
			this.dashboard.undo();
		}

		_onUnSupportedPrompt(msgKey) {
			let dlg = new MessageDialog({
				title: LiveWidgetStringResources.get('unSupportedPrompt'),
				message: LiveWidgetStringResources.get(msgKey),
				onCloseCallback: this._onCancelPromptDialog.bind(this)
			});
			dlg.render();
			return Promise.reject(new Error(this.UNSUPPORTED_PROMPT_TYPE));
		}

		_buildPromptInfoList(specList, visualization) {
			let infoList = [];
			const savedPrompts = visualization && visualization.getSavedPrompts();
			_.each(specList, spec => {
				const savedSpec = savedPrompts && savedPrompts.getPromptSpec(spec.name);
				let info;
				if (savedSpec) {
					info = _.pick(savedSpec, this.SAVED_PROMPT_PROPERTIES);
					if (spec.capabilities.optional && info.values[0] && info.values[0].mun) {
						this._updateInfo(info, { values: [info.values[0].mun], showView: false });
					} else if (spec.rePrompt) {
						// Flag used to differentiate re-prompt or re-open saved dashboard.
						// Saved prompts should not be re-prompted unless click on edit prompt button.
						// showView is undefined by default.
						this._updateInfo(info, { requestSpec: spec, rePrompt: true });
					}

				} else {
					info = _.pick(spec, this.PROMPT_PROPERTIES);
					// Add optional prompt default value and save it
					if (spec.capabilities.optional) {
						this._updateInfo(info, { values: ['nil'], showView: false });
					} else {
						this._updateInfo(info, { requestSpec: spec });
					}
				}
				infoList.push(info);
			});

			return infoList;
		}

		_updateInfo(info, { requestSpec, values, showView = true, rePrompt = false }) {
			if (values) {
				info.values = values;
			}
			info.showView = showView;
			info.rePrompt = rePrompt;
			if (showView) {
				info.promptModuleName = this._getPromptModuleName(requestSpec);
				info.columnId = info.modelFilterItem;
			}
		}

		/*
		 * Returns the prompt dialog type.
		 */
		_getPromptModuleName(spec) {
			let promptModuleName;
			switch (spec.dataType) {
				case 'xsdDate':
				case 'xsdDateTime':
				case 'xsdTime':
					if (spec.capabilities.multivalued && spec.capabilities.discreteValue && spec.modelFilterItem) {
						promptModuleName = this.SEARCH_AND_SELECT;
					} else {
						promptModuleName = this.CALENDAR;
					}
					break;

				case 'xsdInt':
				case 'xsdLong':
				case 'xsdShort':
				case 'xsdFloat':
				case 'xsdDecimal':
				case 'xsdDouble':
					if (spec.modelFilterItem) {
						if (spec.capabilities.multivalued) {
							if (spec.capabilities.discreteValue) {
								promptModuleName = this.SEARCH_AND_SELECT;
							} else {
								promptModuleName = this.RANGE;
							}
						} else {
							promptModuleName = this.INPUT_BOX;
						}
					} else {
						promptModuleName = this.INPUT_BOX;
					}
					break;

				case 'xsdString':
					if (spec.modelFilterItem) {
						promptModuleName = this.SEARCH_AND_SELECT;
					} else {
						promptModuleName = this.INPUT_BOX;
					}
					break;

				default:
					promptModuleName = this.INPUT_BOX;

			}

			return promptModuleName;
		}

		_showPromptView(dlgOptions, specNameList, dataSourceId, pendingViewPromise) {
			const id = dlgOptions.info.name;
			let promptViewPromise = this._inProgressMap[id];
			if (!promptViewPromise) {
				promptViewPromise = pendingViewPromise || Promise.defer();
				dlgOptions.promptViewPromise = promptViewPromise;
				if (this._isPrompting()) {
					// Pending prompt won't return a promise until its turn. Pending prompt view promise is
					// added to local cache in this._executeNextPrompt()
					let pendingPrompt = _.find(this._pendingPrompts, prompt => prompt.id === id);
					if (pendingPrompt) {
						promptViewPromise = pendingPrompt.pendingViewPromise;
					} else {
						pendingPrompt = {
							id: id,
							promptFunction: this._showPromptView.bind(this, dlgOptions, specNameList, dataSourceId, promptViewPromise),
							pendingViewPromise: promptViewPromise
						};
						this._pendingPrompts.push(pendingPrompt);

					}
				} else {
					this._inProgressMap[id] = promptViewPromise;
					dlgOptions.dataSourceId = dataSourceId;
					this._prepareAndOpenDialog(dlgOptions, specNameList, dataSourceId);
				}
			}
			return promptViewPromise.promise;
		}

		_prepareAndOpenDialog(dlgOptions, specNameList) {
			const label = dlgOptions.info.caption || dlgOptions.info.name;
			dlgOptions.viewTitle = StringResources.get('promptControlTitle', {
				paramLabel: label
			});
			dlgOptions.viewOptions = _.pick(dlgOptions.info, [
				'promptModuleName',
				'name',
				'dataType',
				'capabilities'
			]);
			dlgOptions.viewOptions.viewTitle = dlgOptions.viewTitle;

			switch (dlgOptions.info.promptModuleName) {
				case this.INPUT_BOX:
					this.promptDialog = new TextInputPromptDialog({
						dashboard: this.dashboard,
						name: dlgOptions.info.name,
						dataType: dlgOptions.info.dataType,
						values: dlgOptions.info.values,
						onOk: this.onOk.bind(this, dlgOptions),
						onCancel: this.onCancel.bind(this, dlgOptions, specNameList)
					});
					return Promise.resolve(this.promptDialog.open());
				case this.CALENDAR:
					this.promptDialog = new DateTimePromptDialog({
						dashboard: this.dashboard,
						name: dlgOptions.info.name,
						dataSourceId: dlgOptions.dataSourceId,
						columnId: dlgOptions.info.modelFilterItem,
						dataType: dlgOptions.info.dataType,
						values: dlgOptions.info.values,
						isRangePrompt: dlgOptions.info.capabilities.multivalued && !dlgOptions.info.capabilities.discreteValue,
						onOk: this.onOk.bind(this, dlgOptions),
						onCancel: this.onCancel.bind(this, dlgOptions, specNameList)
					});
					return Promise.resolve(this.promptDialog.open());
				case this.SEARCH_AND_SELECT: {
					this.promptDialog = new NonNumericPromptDialog({
						dashboard: this.dashboard,
						name: dlgOptions.info.name,
						dataSourceId: dlgOptions.dataSourceId,
						columnId: dlgOptions.info.modelFilterItem,
						singleSelect: !dlgOptions.info.capabilities.multivalued,
						values: dlgOptions.info.values,
						onOk: this.onOk.bind(this, dlgOptions),
						onCancel: this.onCancel.bind(this, dlgOptions, specNameList)
					});
					return Promise.resolve(this.promptDialog.open());
				}
				case this.RANGE: {
					this.promptDialog = new NumericPromptDialog({
						columnId: dlgOptions.info.modelFilterItem,
						dashboard: this.dashboard,
						name: dlgOptions.info.name,
						dataSourceId: dlgOptions.dataSourceId,
						values: dlgOptions.info.values,
						onOk: this.onOk.bind(this, dlgOptions),
						onCancel: this.onCancel.bind(this, dlgOptions, specNameList)
					});
					return Promise.resolve(this.promptDialog.open());
				}
				default:
			}
		}

		onOk(options, resolvedValues) {
			this._removeInProgressAndExecuteNext(options.info.name);
			let resolvedSpec = options.info;
			resolvedSpec.values = _.map(resolvedValues, value => {
				if (typeof value === 'object') {
					return _.pick(value, ['value', 'label']);
				} else {
					return {
						'value': value,
						'label': value
					};
				}

			});

			return options.promptViewPromise.resolve(resolvedSpec);
		}

		onCancel(options, specNameList) {
			// Cancel one prompt dialog should trigger cancel of all requried prompt actions since
			// all of the non-optional prompts are required for the query to run.
			this._cleanPromptCache(options.info.name, specNameList);

			this.promptDialog.hide();
			if (!options.info.rePrompt) {
				// drag and drop prompts are combined actions need to undo this action.
				// E.g. Dnd prompts to local filter, slots, canvas, or tab filter zone triggers multiple
				// events. If cancel happened in on Dnd prompts, need to undo the actions.
				// But in case of re-prompts, only need to close the prompts dialog.
				this._onCancelPromptDialog();
			}

			const error = new Error(this.CANCEL);
			error.name = options.info.name;
			return options.promptViewPromise.reject(error);
		}

		enableOk(bEnabled) {
			this.promptDialog.enableOk(bEnabled);
		}

		_isPrompting() {
			return _.size(this._inProgressMap) > 0;
		}

		_executeNextPrompt() {
			if (this._pendingPrompts.length > 0) {
				const nextPrompt = this._pendingPrompts.shift();
				return nextPrompt.promptFunction();
			}
		}

		_removeInProgressAndExecuteNext(id) {
			delete this._inProgressMap[id];
			this._executeNextPrompt();
		}

		_cleanPromptCache(id, specNameList) {
			// Cancel all current requests
			this._pendingPrompts = _.filter(this._pendingPrompts, pendingPrompt =>
				specNameList.indexOf(pendingPrompt.id) === -1
			);

			delete this._inProgressMap[id];
		}
	}

	return Prompts;

});
